// Feature: docs/features/nma-workflow-dashboard.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Select,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Badge,
  Divider,
  VStack,
  HStack
} from '@chakra-ui/react';
import { DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const NMAWorkflowDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const [procData, setProcData] = useState({ summary: [], details: [] });
  const [daysFilter, setDaysFilter] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // SFTP Variance State
  const [varianceData, setVarianceData] = useState(null);
  const [selectedVariance, setSelectedVariance] = useState(null); // 'ingestion', 'outbound', or 'bot'

  // Case Lifecycle Search State
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchCaseId, setSearchCaseId] = useState('');
  const [lifecycleData, setLifecycleData] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const handleSearch = async () => {
    if (!searchCaseId.trim()) return;
    
    setIsSearching(true);
    setSearchError(null);
    onOpen();
    
    try {
      const res = await callApi(`/nma/case-lifecycle/${searchCaseId.trim()}`, accessToken, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setLifecycleData(data);
      } else {
        setSearchError('Failed to fetch case lifecycle data.');
      }
    } catch (err) {
      console.error('Error fetching case lifecycle:', err);
      setSearchError('An error occurred while searching.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!accessToken) return;
        
        const [procRes, varianceRes] = await Promise.all([
          callApi(`/nma/workflow-procedure-data?days=${daysFilter}`, accessToken, { method: 'GET' }),
          callApi(`/nma/sftp_variances`, accessToken, { method: 'GET' })
        ]);

        if (procRes.ok) {
          const procDataJson = await procRes.json();
          setProcData(procDataJson);
        } else {
          throw new Error('Failed to fetch procedure data');
        }

        if (varianceRes.ok) {
          const varianceJson = await varianceRes.json();
          if (varianceJson.status === 'success') {
            setVarianceData(varianceJson.data);
          }
        } else {
          console.error('Failed to fetch variance data');
        }
      } catch (err) {
        console.error('Error fetching workflow summary:', err);
        setError('Failed to load workflow summary data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, daysFilter]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as MM/DD/YYYY
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const mapStateToLabel = (state) => {
    switch(state) {
      case 'revedy_review': return 'Step 1: Error';
      case 'pending_zinnia': return 'Step 1: Revedy Complete';
      case 'zinnia_exceptions': return 'Step 2: Zinnia upload error';
      case 'zinnia_processed': return 'Step 2: Zinnia upload complete';
      case 'unknown':
      case 'success':
      default: return 'Step 1: In Progress';
    }
  };

  const downloadCSV = () => {
    if (!procData.details || procData.details.length === 0) return;
    
    const headers = ['Case ID', 'Procedure Date', 'Date Coded (Create Date)', 'Current State', 'State Timestamp'];
    const csvContent = [
      headers.join(','),
      ...procData.details.map(row => 
        `${row.case_id},${formatDate(row.procedure_date)},${formatDate(row.date_coded)},"${mapStateToLabel(row.current_state)}",${formatDate(row.state_timestamp)}`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `NMA_Procedure_Data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadVarianceCSV = (dataArray, filename, type) => {
    if (!dataArray || dataArray.length === 0) return;
    
    let headers = [];
    let rows = [];

    if (type === 'ingestion' || type === 'bot') {
      headers = ['Case ID', 'Date of Surgery', 'Timestamp', 'Directory'];
      rows = dataArray.map(row => 
        `${row.case_id},${formatDate(row.date_of_surgery)},${row.timestamp ? new Date(row.timestamp).toLocaleString() : 'N/A'},${row.directory}`
      );
    } else if (type === 'outbound' || type === 'additional') {
      headers = ['Case ID', 'Case Name', 'Date of Surgery', 'Create Date'];
      rows = dataArray.map(row => 
        `${row.case_id},"${row.case_name || ''}",${formatDate(row.date_of_surgery)},${row.create_date ? new Date(row.create_date).toLocaleString() : 'N/A'}`
      );
    } else if (type === 'excluded') {
      headers = ['Case ID', 'Date of Surgery', 'Timestamp', 'Tech Status', 'Claim Number', 'Insurance Type', 'Carrier Name', 'Claim Status'];
      rows = dataArray.map(row => 
        `${row.case_id},${formatDate(row.date_of_surgery)},${row.timestamp ? new Date(row.timestamp).toLocaleString() : 'N/A'},"${row.tech_status || ''}","${row.claim_number || ''}","${row.insurance_type || ''}","${row.carrier_name || ''}","${row.claim_status || ''}"`
      );
    }

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCaseClick = (caseId) => {
    setSearchCaseId(caseId);
    // We need to wait for state to update before searching, or pass it directly
    // Since handleSearch uses the state, we'll create a wrapper that takes the ID directly
    searchSpecificCase(caseId);
  };

  const searchSpecificCase = async (caseId) => {
    if (!caseId.trim()) return;
    
    setIsSearching(true);
    setSearchError(null);
    onOpen();
    
    try {
      const res = await callApi(`/nma/case-lifecycle/${caseId.trim()}`, accessToken, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setLifecycleData(data);
      } else {
        setSearchError('Failed to fetch case lifecycle data.');
      }
    } catch (err) {
      console.error('Error fetching case lifecycle:', err);
      setSearchError('An error occurred while searching.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="container.xl" mx="auto" py={8} px={4}>

        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">
            NMA Workflow Dashboard
          </Heading>
          <Box w="300px">
            <InputGroup size="md">
              <Input
                pr="4.5rem"
                placeholder="Search Case ID..."
                value={searchCaseId}
                onChange={(e) => setSearchCaseId(e.target.value)}
                onKeyPress={handleKeyPress}
                bg="white"
                shadow="sm"
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={handleSearch} colorScheme="brand" isLoading={isSearching}>
                  <SearchIcon />
                </Button>
              </InputRightElement>
            </InputGroup>
          </Box>
        </Flex>


        <Tabs colorScheme="brand" variant="enclosed">
          <TabList>
            <Tab fontWeight="medium">Workflow Summary</Tab>
            <Tab fontWeight="medium">SFTP Variances</Tab>
            <Tab fontWeight="medium">Excluded Cases Review</Tab>
            <Tab fontWeight="medium">Additional Cases</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel px={0} pt={6}>
              <Flex justify="space-between" align="center" mb={6}>
                <Box w="200px">
                  <Select 
                    value={daysFilter} 
                    onChange={(e) => setDaysFilter(Number(e.target.value))}
                    bg="white"
                    shadow="sm"
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={14}>Last 14 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                  </Select>
                </Box>
                <Button 
                  leftIcon={<DownloadIcon />} 
                  colorScheme="brand" 
                  onClick={downloadCSV}
                  isDisabled={!procData.details || procData.details.length === 0}
                >
                  Download Detailed CSV
                </Button>
              </Flex>

              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white" mb={8}>
                <CardBody>
                  <Box h="300px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[...procData.summary].reverse()} // Reverse to show chronological order left-to-right
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis 
                          dataKey="procedure_date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          minTickGap={30}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip 
                          labelFormatter={formatDate}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                        <Legend />
                        <Bar dataKey="other_pending" name="Step 1: In Progress" stackId="a" fill="#A0AEC0" />
                        <Bar dataKey="revedy_review" name="Step 1: Error" stackId="a" fill="#DD6B20" />
                        <Bar dataKey="pending_zinnia" name="Step 1: Revedy Complete" stackId="a" fill="#ECC94B" />
                        <Bar dataKey="zinnia_exceptions" name="Step 2: Zinnia upload error" stackId="a" fill="#E53E3E" />
                        <Bar dataKey="zinnia_processed" name="Step 2: Zinnia upload complete" stackId="a" fill="#38A169" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                <CardBody p={0}>
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Procedure Date</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Total Cases</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Step 1: In Progress</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Step 1: Error</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Step 1: Revedy Complete</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Step 2: Zinnia upload error</Th>
                          <Th color="gray.500" fontSize="xs" textTransform="uppercase" isNumeric>Step 2: Zinnia upload complete</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {!procData.summary || procData.summary.length === 0 ? (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8} color="gray.500">
                              No procedure data available for the selected timeframe.
                            </Td>
                          </Tr>
                        ) : (
                          procData.summary.map((row) => (
                            <Tr key={row.procedure_date} _hover={{ bg: 'gray.50' }}>
                              <Td fontWeight="medium" color="gray.700">{formatDate(row.procedure_date)}</Td>
                              <Td isNumeric color="gray.600" fontWeight="bold">{row.total_parsed}</Td>
                              <Td isNumeric color={row.other_pending > 0 ? 'gray.500' : 'gray.600'}>{row.other_pending}</Td>
                              <Td isNumeric color={row.revedy_review > 0 ? 'orange.500' : 'gray.600'}>{row.revedy_review}</Td>
                              <Td isNumeric color={row.pending_zinnia > 0 ? 'yellow.600' : 'gray.600'}>{row.pending_zinnia}</Td>
                              <Td isNumeric color={row.zinnia_exceptions > 0 ? 'red.500' : 'gray.600'} fontWeight={row.zinnia_exceptions > 0 ? 'bold' : 'normal'}>{row.zinnia_exceptions}</Td>
                              <Td isNumeric color={row.zinnia_processed > 0 ? 'green.500' : 'gray.600'} fontWeight={row.zinnia_processed > 0 ? 'bold' : 'normal'}>{row.zinnia_processed}</Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </CardBody>
              </Card>
            </TabPanel>

            <TabPanel px={0} pt={6}>
              {varianceData ? (
                <>
                  <Flex justify="space-between" align="center" mb={6}>
                    <Text color="gray.600">
                      This tab shows real-time drop-offs (variances) between the SFTP directories and our database. 
                      Click on a card to view the specific cases.
                    </Text>
                    {selectedVariance && (
                      <Button 
                        size="sm"
                        leftIcon={<DownloadIcon />} 
                        colorScheme="brand" 
                        variant="outline"
                        onClick={() => downloadVarianceCSV(
                          varianceData[`${selectedVariance}${selectedVariance === 'bot' ? '_pending' : '_variance'}`].cases, 
                          `NMA_${selectedVariance}_Variance`, 
                          selectedVariance
                        )}
                      >
                        Export CSV
                      </Button>
                    )}
                  </Flex>
                  
                  <Flex gap={6} mb={8}>
                    <Card 
                      flex={1} 
                      cursor="pointer" 
                      onClick={() => setSelectedVariance('ingestion')}
                      border="2px solid"
                      borderColor={selectedVariance === 'ingestion' ? 'brand.500' : 'transparent'}
                      bg={selectedVariance === 'ingestion' ? 'brand.50' : 'white'}
                      shadow="sm"
                      _hover={{ shadow: 'md' }}
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel fontSize="sm" color="gray.500" textTransform="uppercase">Ingestion Variance</StatLabel>
                          <StatNumber fontSize="3xl" color={varianceData.ingestion_variance.count > 0 ? 'red.500' : 'green.500'}>
                            {varianceData.ingestion_variance.count}
                          </StatNumber>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            Expected (in Reports) vs. Processed (in DB)
                          </Text>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card 
                      flex={1} 
                      cursor="pointer" 
                      onClick={() => setSelectedVariance('outbound')}
                      border="2px solid"
                      borderColor={selectedVariance === 'outbound' ? 'brand.500' : 'transparent'}
                      bg={selectedVariance === 'outbound' ? 'brand.50' : 'white'}
                      shadow="sm"
                      _hover={{ shadow: 'md' }}
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel fontSize="sm" color="gray.500" textTransform="uppercase">Outbound Variance</StatLabel>
                          <StatNumber fontSize="3xl" color={varianceData.outbound_variance.count > 0 ? 'orange.500' : 'green.500'}>
                            {varianceData.outbound_variance.count}
                          </StatNumber>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            Processed (last 7 days) vs. Uploaded
                          </Text>
                        </Stat>
                      </CardBody>
                    </Card>

                    <Card 
                      flex={1} 
                      cursor="pointer" 
                      onClick={() => setSelectedVariance('bot')}
                      border="2px solid"
                      borderColor={selectedVariance === 'bot' ? 'brand.500' : 'transparent'}
                      bg={selectedVariance === 'bot' ? 'brand.50' : 'white'}
                      shadow="sm"
                      _hover={{ shadow: 'md' }}
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel fontSize="sm" color="gray.500" textTransform="uppercase">Bot Pending</StatLabel>
                          <StatNumber fontSize="3xl" color={varianceData.bot_pending.count > 0 ? 'blue.500' : 'green.500'}>
                            {varianceData.bot_pending.count}
                          </StatNumber>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            Uploaded vs. Bot Processed
                          </Text>
                        </Stat>
                      </CardBody>
                    </Card>
                  </Flex>

                  {selectedVariance && (
                    <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                      <CardBody p={0}>
                        <Box overflowX="auto">
                          <Table variant="simple">
                            <Thead bg="gray.50">
                              <Tr>
                                <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Case ID</Th>
                                <Th color="gray.500" fontSize="xs" textTransform="uppercase">Date of Surgery</Th>
                                {selectedVariance === 'outbound' ? (
                                  <>
                                    <Th color="gray.500" fontSize="xs" textTransform="uppercase">Case Name</Th>
                                    <Th color="gray.500" fontSize="xs" textTransform="uppercase">Create Date</Th>
                                  </>
                                ) : (
                                  <>
                                    <Th color="gray.500" fontSize="xs" textTransform="uppercase">Directory</Th>
                                    <Th color="gray.500" fontSize="xs" textTransform="uppercase">Timestamp</Th>
                                  </>
                                )}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {varianceData[`${selectedVariance}${selectedVariance === 'bot' ? '_pending' : '_variance'}`].cases.length === 0 ? (
                                <Tr>
                                  <Td colSpan={4} textAlign="center" py={8} color="gray.500">
                                    No cases found for this variance.
                                  </Td>
                                </Tr>
                              ) : (
                                varianceData[`${selectedVariance}${selectedVariance === 'bot' ? '_pending' : '_variance'}`].cases.map((c, idx) => (
                                  <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                    <Td fontWeight="medium" color="brand.500">
                                      <Button variant="link" colorScheme="brand" onClick={() => handleCaseClick(c.case_id)}>
                                        {c.case_id}
                                      </Button>
                                    </Td>
                                    <Td color="gray.600">{formatDate(c.date_of_surgery)}</Td>
                                    {selectedVariance === 'outbound' ? (
                                      <>
                                        <Td color="gray.600">{c.case_name}</Td>
                                        <Td color="gray.600">{c.create_date ? new Date(c.create_date).toLocaleString() : 'N/A'}</Td>
                                      </>
                                    ) : (
                                      <>
                                        <Td color="gray.600">{c.directory}</Td>
                                        <Td color="gray.600">{c.timestamp ? new Date(c.timestamp).toLocaleString() : 'N/A'}</Td>
                                      </>
                                    )}
                                  </Tr>
                                ))
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </CardBody>
                    </Card>
                  )}
                </>
              ) : (
                <Flex justify="center" align="center" py={12}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              )}
            </TabPanel>

            <TabPanel px={0} pt={6}>
              {varianceData ? (
                <>
                  <Text mb={6} color="gray.600">
                    This tab shows detailed Zinnia claim data for cases that are sitting in the inbound SFTP folders but have not been processed into our database. 
                    <br/><br/>
                    <strong>Note:</strong> To reduce noise from natural coding delays, this list only includes cases where the Date of Surgery is at least 8 days in the past.
                  </Text>
                  
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Heading size="md" color="gray.700">
                        Actionable Exclusions (Has Pro Claim)
                      </Heading>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        These cases have at least one Professional ('P') claim but were not processed. They should be investigated.
                      </Text>
                    </Box>
                    <Button 
                      size="sm"
                      leftIcon={<DownloadIcon />} 
                      colorScheme="brand" 
                      variant="outline"
                      onClick={() => downloadVarianceCSV(varianceData.excluded_cases?.actionable?.cases, 'NMA_Actionable_Exclusions', 'excluded')}
                      isDisabled={!varianceData.excluded_cases?.actionable?.cases || varianceData.excluded_cases.actionable.cases.length === 0}
                    >
                      Export CSV
                    </Button>
                  </Flex>
                  <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white" mb={8}>
                    <CardBody p={0}>
                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Case ID</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Date of Surgery</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Timestamp</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Tech Status</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Claim Number</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Insurance Type</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Carrier Name</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Claim Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {!varianceData.excluded_cases?.actionable?.cases || varianceData.excluded_cases.actionable.cases.length === 0 ? (
                              <Tr>
                                <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                                  No actionable exclusions found.
                                </Td>
                              </Tr>
                            ) : (
                              varianceData.excluded_cases.actionable.cases.map((c, idx) => (
                                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                  <Td fontWeight="medium" color="brand.500">
                                    <Button variant="link" colorScheme="brand" onClick={() => handleCaseClick(c.case_id)}>
                                      {c.case_id}
                                    </Button>
                                  </Td>
                                  <Td color="gray.600">{formatDate(c.date_of_surgery)}</Td>
                                  <Td color="gray.600">{c.timestamp ? new Date(c.timestamp).toLocaleString() : 'N/A'}</Td>
                                  <Td color="gray.600">{c.tech_status || 'N/A'}</Td>
                                  <Td color="gray.600">{c.claim_number || 'N/A'}</Td>
                                  <Td color="gray.600">{c.insurance_type || 'N/A'}</Td>
                                  <Td color="gray.600">{c.carrier_name || 'N/A'}</Td>
                                  <Td color="gray.600">{c.claim_status || 'N/A'}</Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </CardBody>
                  </Card>

                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Heading size="md" color="gray.700">
                        Expected Exclusions (Tech Claim Only)
                      </Heading>
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        These cases only have Technical ('T') claims. Our pipeline intentionally ignores them.
                      </Text>
                    </Box>
                    <Button 
                      size="sm"
                      leftIcon={<DownloadIcon />} 
                      colorScheme="brand" 
                      variant="outline"
                      onClick={() => downloadVarianceCSV(varianceData.excluded_cases?.tech_only?.cases, 'NMA_Tech_Only_Exclusions', 'excluded')}
                      isDisabled={!varianceData.excluded_cases?.tech_only?.cases || varianceData.excluded_cases.tech_only.cases.length === 0}
                    >
                      Export CSV
                    </Button>
                  </Flex>
                  <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                    <CardBody p={0}>
                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Case ID</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Date of Surgery</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Timestamp</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Tech Status</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Claim Number</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Insurance Type</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Carrier Name</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Claim Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {!varianceData.excluded_cases?.tech_only?.cases || varianceData.excluded_cases.tech_only.cases.length === 0 ? (
                              <Tr>
                                <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                                  No tech-only exclusions found.
                                </Td>
                              </Tr>
                            ) : (
                              varianceData.excluded_cases.tech_only.cases.map((c, idx) => (
                                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                  <Td fontWeight="medium" color="brand.500">
                                    <Button variant="link" colorScheme="brand" onClick={() => handleCaseClick(c.case_id)}>
                                      {c.case_id}
                                    </Button>
                                  </Td>
                                  <Td color="gray.600">{formatDate(c.date_of_surgery)}</Td>
                                  <Td color="gray.600">{c.timestamp ? new Date(c.timestamp).toLocaleString() : 'N/A'}</Td>
                                  <Td color="gray.600">{c.tech_status || 'N/A'}</Td>
                                  <Td color="gray.600">{c.claim_number || 'N/A'}</Td>
                                  <Td color="gray.600">{c.insurance_type || 'N/A'}</Td>
                                  <Td color="gray.600">{c.carrier_name || 'N/A'}</Td>
                                  <Td color="gray.600">{c.claim_status || 'N/A'}</Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </CardBody>
                  </Card>
                </>
              ) : (
                <Flex justify="center" align="center" py={12}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              )}
            </TabPanel>

            <TabPanel px={0} pt={6}>
              {varianceData ? (
                <>
                  <Text mb={6} color="gray.600">
                    This tab shows cases that have been coded in our database within the last 7 days, but do not have a corresponding inbound SFTP log (e.g., they bypassed the standard Reports folders).
                    <br/><br/>
                    <strong>Note:</strong> To reduce noise from natural coding delays, this list only includes cases where the Date of Surgery is at least 8 days in the past.
                  </Text>
                  
                  <Flex gap={6} mb={8} align="center">
                    <Card 
                      flex={1} 
                      border="2px solid"
                      borderColor="brand.500"
                      bg="brand.50"
                      shadow="sm"
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel fontSize="sm" color="gray.500" textTransform="uppercase">Additional Cases</StatLabel>
                          <StatNumber fontSize="3xl" color="brand.500">
                            {varianceData.additional_cases?.count || 0}
                          </StatNumber>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            Coded without inbound log (Last 7 days)
                          </Text>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Box>
                      <Button 
                        leftIcon={<DownloadIcon />} 
                        colorScheme="brand" 
                        variant="outline"
                        onClick={() => downloadVarianceCSV(varianceData.additional_cases?.cases, 'NMA_Additional_Cases', 'additional')}
                        isDisabled={!varianceData.additional_cases?.cases || varianceData.additional_cases.cases.length === 0}
                      >
                        Export CSV
                      </Button>
                    </Box>
                  </Flex>

                  <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
                    <CardBody p={0}>
                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase" py={4}>Case ID</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Date of Surgery</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Case Name</Th>
                              <Th color="gray.500" fontSize="xs" textTransform="uppercase">Create Date</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {!varianceData.additional_cases?.cases || varianceData.additional_cases.cases.length === 0 ? (
                              <Tr>
                                <Td colSpan={4} textAlign="center" py={8} color="gray.500">
                                  No additional cases found.
                                </Td>
                              </Tr>
                            ) : (
                              varianceData.additional_cases.cases.map((c, idx) => (
                                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                  <Td fontWeight="medium" color="brand.500">
                                    <Button variant="link" colorScheme="brand" onClick={() => handleCaseClick(c.case_id)}>
                                      {c.case_id}
                                    </Button>
                                  </Td>
                                  <Td color="gray.600">{formatDate(c.date_of_surgery)}</Td>
                                  <Td color="gray.600">{c.case_name}</Td>
                                  <Td color="gray.600">{c.create_date ? new Date(c.create_date).toLocaleString() : 'N/A'}</Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </CardBody>
                  </Card>
                </>
              ) : (
                <Flex justify="center" align="center" py={12}>
                  <Spinner size="xl" color="brand.500" />
                </Flex>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Case Lifecycle Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Case Lifecycle Trace: {searchCaseId}
            {lifecycleData && (
              <Badge ml={4} colorScheme={lifecycleData.current_state.includes('Error') ? 'red' : 'green'} fontSize="sm">
                {lifecycleData.current_state}
              </Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isSearching ? (
              <Flex justify="center" py={10}><Spinner size="xl" color="brand.500" /></Flex>
            ) : searchError ? (
              <Alert status="error"><AlertIcon />{searchError}</Alert>
            ) : lifecycleData ? (
              <VStack align="stretch" spacing={6}>
                
                {/* Core DB Status */}
                <Box>
                  <Heading size="sm" mb={3} color="gray.700">1. Core Database Status</Heading>
                  {lifecycleData.core_data ? (
                    <Card variant="outline" bg="gray.50">
                      <CardBody py={3}>
                        <HStack spacing={8}>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">Internal ID</Text>
                            <Text fontWeight="medium">{lifecycleData.core_data.internal_id}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">Case Name</Text>
                            <Text fontWeight="medium">{lifecycleData.core_data.case_name}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">Procedure Date</Text>
                            <Text fontWeight="medium">{lifecycleData.core_data.procedure_date ? new Date(lifecycleData.core_data.procedure_date).toLocaleDateString() : 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">Create Date</Text>
                            <Text fontWeight="medium">{lifecycleData.core_data.create_date ? new Date(lifecycleData.core_data.create_date).toLocaleString() : 'N/A'}</Text>
                          </Box>
                        </HStack>
                      </CardBody>
                    </Card>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">Case not found in core database.</Text>
                  )}
                </Box>

                <Divider />

                {/* Zinnia Status */}
                <Box>
                  <Heading size="sm" mb={3} color="gray.700">2. Zinnia Status</Heading>
                  {lifecycleData.zinnia_data ? (
                    <Card variant="outline" bg="gray.50">
                      <CardBody py={3}>
                        <HStack spacing={8} mb={4}>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">ZinniaX Case ID</Text>
                            <Text fontWeight="medium">{lifecycleData.zinnia_data.zinniax_case_id}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500" textTransform="uppercase">Tech Status</Text>
                            <Text fontWeight="medium">{lifecycleData.zinnia_data.tech_status}</Text>
                          </Box>
                        </HStack>
                        {lifecycleData.zinnia_data.claims.length > 0 ? (
                          <Table size="sm" variant="simple">
                            <Thead><Tr><Th>Claim #</Th><Th>Type</Th><Th>Carrier</Th><Th>Status</Th></Tr></Thead>
                            <Tbody>
                              {lifecycleData.zinnia_data.claims.map((c, i) => (
                                <Tr key={i}>
                                  <Td>{c.claim_number}</Td>
                                  <Td>{c.payer_type}</Td>
                                  <Td>{c.carrier_name}</Td>
                                  <Td>{c.status}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        ) : (
                          <Text fontSize="sm" color="gray.500">No claims found in Zinnia.</Text>
                        )}
                      </CardBody>
                    </Card>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">Case not found in Zinnia.</Text>
                  )}
                </Box>

                <Divider />

                {/* AI Job History */}
                <Box>
                  <Heading size="sm" mb={3} color="gray.700">3. AI Job History</Heading>
                  {lifecycleData.ai_history && lifecycleData.ai_history.length > 0 ? (
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Timestamp</Th><Th>Analysis Type</Th><Th>Claim Type</Th><Th>Status</Th><Th>CPTs Generated</Th></Tr></Thead>
                      <Tbody>
                        {lifecycleData.ai_history.map((job, i) => (
                          <Tr key={i}>
                            <Td>{new Date(job.timestamp).toLocaleString()}</Td>
                            <Td>{job.analysis_type}</Td>
                            <Td>{job.claim_type}</Td>
                            <Td>
                              <Badge colorScheme={job.has_errors ? 'red' : 'green'}>{job.status}</Badge>
                            </Td>
                            <Td>{job.cpt_codes_generated}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No AI jobs run for this case.</Text>
                  )}
                </Box>

                <Divider />

                {/* SFTP History */}
                <Box>
                  <Heading size="sm" mb={3} color="gray.700">4. SFTP Movement Timeline</Heading>
                  {lifecycleData.sftp_history && lifecycleData.sftp_history.length > 0 ? (
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>Timestamp</Th><Th>Server</Th><Th>Directory</Th></Tr></Thead>
                      <Tbody>
                        {lifecycleData.sftp_history.map((log, i) => (
                          <Tr key={i}>
                            <Td>{new Date(log.timestamp).toLocaleString()}</Td>
                            <Td>{log.server}</Td>
                            <Td fontWeight="medium">{log.directory}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No SFTP logs found.</Text>
                  )}
                </Box>

              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default NMAWorkflowDashboard;
